import { Button } from '../lib/button'
import { useState } from 'react'
import { Icon } from '../lib/icon'
import { HorizontalLine } from '../lib/horizontal-line'
import { QuantityUnit } from '../lib/quantity-unit'
import { CounterInput } from '../lib/counter-input'
import { ExclusiveButtons } from '../lib/exclusive-buttons'
import { InclusiveButtons } from '../lib/inclusive-buttons'
import { Select } from '../lib/select'
import { Multiselect } from '../lib/multiselect'
import { FormField } from '../lib/forms'
import { Dialog } from '../lib/dialog'
import { Drawer } from '../lib/drawer'
import { Accordion, AccordionItem } from '../lib/accordion'
import { Tabs } from '../lib/tabs'
import { Tooltip } from '../lib/tooltip'
import { Paginator } from '../lib/paginator'
import { Table } from '../lib/table'

function PlayGroundPage() {
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)

    const [counterValue, setCounterValue] = useState(0)
    const [exclusiveValue, setExclusiveValue] = useState<string>('B')
    const [inclusiveValue, setInclusiveValue] = useState<string[]>(['A'])
    const [selectValue, setSelectValue] = useState<number | undefined>(undefined)
    const [multiSelectValue, setMultiSelectValue] = useState<number[]>([])

    const [page, setPage] = useState(1)

    return (
        <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
            <h1>React Components Showcase</h1>
            <HorizontalLine />

            <section style={{ marginBottom: '40px' }}>
                <h2>Core Basics</h2>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'flex-start', marginBottom: '16px' }}>
                    <Button variant="primary">Primary Button</Button>
                    <Button>Default Button</Button>
                    <Button processing>Loading Button</Button>
                    <Button size="small" variant="primary">Small</Button>
                </div>

                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span>Icons:</span>
                    <Icon name="check" style={{ fontSize: '24px', color: '#52c41a' }} />
                    <Icon name="bell" style={{ fontSize: '24px' }} />
                    <Icon name="trash" style={{ fontSize: '24px', color: '#ff4d4f' }} />
                </div>
            </section>

            <HorizontalLine />

            <section style={{ marginBottom: '40px' }}>
                <h2>Forms & Inputs</h2>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                    <div>
                        <FormField label="Quantity" hint="Enter the quantity in kilograms" required>
                            <QuantityUnit placeholder="E.g. 15" unit="kg" />
                        </FormField>

                        <FormField label="Counter" required>
                            <CounterInput min={0} max={10} value={counterValue} onChange={setCounterValue} />
                        </FormField>

                        <FormField label="Exclusive Choice">
                            <ExclusiveButtons
                                items={['A', 'B', 'C']}
                                value={exclusiveValue}
                                onChange={setExclusiveValue}
                            />
                        </FormField>

                        <FormField label="Inclusive Choice">
                            <InclusiveButtons
                                items={['A', 'B', 'C']}
                                value={inclusiveValue}
                                onChange={setInclusiveValue}
                            />
                        </FormField>
                    </div>

                    <div>
                        <FormField label="Select an Item">
                            <Select
                                items={[{ id: 1, name: 'Apple' }, { id: 2, name: 'Banana' }]}
                                value={selectValue}
                                onChange={setSelectValue}
                                identityFn={i => i.id}
                                textFn={i => i.name}
                                placeholder="Choose fruit"
                            />
                        </FormField>

                        <FormField label="Select Multiple Items">
                            <Multiselect
                                items={[{ id: 1, name: 'Apple' }, { id: 2, name: 'Banana' }, { id: 3, name: 'Cherry' }]}
                                value={multiSelectValue}
                                onChange={setMultiSelectValue}
                                identityFn={i => i.id}
                                textFn={i => i.name}
                                placeholder="Choose fruits"
                            />
                        </FormField>
                    </div>
                </div>
            </section>

            <HorizontalLine />

            <section style={{ marginBottom: '40px' }}>
                <h2>Data Display & Layout</h2>

                <div style={{ marginBottom: '24px' }}>
                    <h3>Tabs</h3>
                    <Tabs
                        items={[
                            { key: 't1', label: 'Tab 1', content: <div style={{ padding: '16px' }}>Content of Tab 1</div> },
                            { key: 't2', label: 'Tab 2', content: <div style={{ padding: '16px' }}>Content of Tab 2</div> },
                            { key: 't3', label: 'Disabled Tab', content: <div />, disabled: true }
                        ]}
                    />
                </div>

                <div style={{ marginBottom: '24px' }}>
                    <h3>Accordion</h3>
                    <Accordion>
                        <AccordionItem title="Advanced Settings" defaultOpen>
                            <p>These are advanced settings that you can configure.</p>
                        </AccordionItem>
                        <AccordionItem title="User Preferences">
                            <p>Preferences configuration goes here.</p>
                        </AccordionItem>
                    </Accordion>
                </div>

                <div style={{ display: 'flex', gap: '24px', alignItems: 'center', justifyContent: 'flex-start', marginBottom: '24px' }}>
                  <Tooltip content="This is a simple tooltip!" placement="top">
                    <span style={{ borderBottom: '1px dotted #ccc', cursor: 'pointer' }}>Hover me for Tooltip</span>
                  </Tooltip>

                  <Paginator current={page} total={100} pageSize={10} onChange={setPage} />
                </div>

                <div>
                    <h3>Table</h3>
                    <Table
                        data={[
                            { id: '1', name: 'Maciej', role: 'Admin' },
                            { id: '2', name: 'John', role: 'User' }
                        ]}
                        columns={[
                            { key: 'id', title: 'ID', width: 60 },
                            { key: 'name', title: 'Name' },
                            { key: 'role', title: 'Role' }
                        ]}
                        rowKey={(item) => item.id}
                    />
                </div>
            </section>

            <HorizontalLine />

            <section style={{ marginBottom: '40px' }}>
                <h2>Modals & Overlays</h2>
                <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-start' }}>
                  <Button variant="primary" onClick={() => setIsDialogOpen(true)}>Open Dialog</Button>
                  <Button onClick={() => setIsDrawerOpen(true)}>Open Drawer</Button>
                </div>

                <Dialog
                    open={isDialogOpen}
                    onClose={() => setIsDialogOpen(false)}
                    title="Important Action"
                    footer={<Button variant="primary" onClick={() => setIsDialogOpen(false)}>Confirm</Button>}
                    footerExtras={<Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>}
                >
                    <p>Please confirm that you want to perform this action. This cannot be undone.</p>
                </Dialog>

                <Drawer
                    open={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    title="Sidebar Settings"
                    footer={<Button variant="primary" onClick={() => setIsDrawerOpen(false)}>Save Settings</Button>}
                >
                    <FormField label="Feature Flag" hint="Enable cutting edge features">
                        <InclusiveButtons items={['Enabled']} value={[]} />
                    </FormField>
                </Drawer>
            </section>
        </div>
    )
}

export default PlayGroundPage
